<template>
  <div class="flex flex-wrap items-center gap-2">
    <div class="flex flex-col max-w-[250px]">
      <input
        v-if="allowCustom"
        v-model="tagInput"
        @keyup.enter="handleCustomTag"
        class="tag-input"
        placeholder='Add a new classification!' />
      <p v-if="v$.tagInput.$error" class="text-red-500 text-xs mt-1">
        {{ v$.tagInput.$errors[0]?.$message }}
      </p>
    </div>
    <Tag
      v-for="tag in tags"
      :key="tag"
      :label="tag"
      :isSelected="modelValue.map(t => t.toLowerCase()).includes(tag.toLowerCase())"
      :isCustom="isCustomTag(tag)"
      @click="toggleTag(tag)"
      @remove="handleRemove(tag)" />
  </div>
</template>

<script setup>
import Tag from '@/components/Classify/Tag.vue';
import { filterTags } from '@/utils/consts.js';
import useVuelidate from '@vuelidate/core';
import { maxLength, helpers } from '@vuelidate/validators';
import { ref } from 'vue';
const props = defineProps({
  modelValue: {
    type: Array,
    required: true,
  },
  tags: {
    type: Array,
    required: true,
  },
  allowCustom: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:modelValue', 'add-tag', 'remove-tag']);

const tagInput = ref('');

const rules = {
  tagInput: {
    maxLength: helpers.withMessage('Tag must be 15 characters or fewer', maxLength(15)),
    onlyLetters: helpers.withMessage('Tag can only contain letters and numbers', helpers.regex(/^[A-Za-z0-9_]+$/)),
  },
};

const v$ = useVuelidate(rules, { tagInput });

const defaultTags = filterTags
  .filter(el => el.label && !['Following', 'Monitoring', 'All'].includes(el.label))
  .map(el => el.label.toLowerCase());

const isCustomTag = tag => !defaultTags.includes(tag.toLowerCase());

const toggleTag = tag => {
  const newValue = props.modelValue.includes(tag)
    ? props.modelValue.filter(t => t !== tag)
    : [...props.modelValue, tag];
  emit('update:modelValue', newValue);
};

const handleCustomTag = async () => {
  const customTag = tagInput.value.trim();
  if (!customTag) return;

  await v$.value.$validate();
  if (v$.value.$invalid) return;

  const lower = customTag.toLowerCase();
  const tagExists = props.tags.some(t => t.toLowerCase() === lower);

  if (!tagExists) {
    emit('add-tag', customTag);
  }

  const existing = props.tags.find(t => t.toLowerCase() === lower) || customTag;

  if (!props.modelValue.some(t => t.toLowerCase() === lower)) {
    emit('update:modelValue', [...props.modelValue, existing]);
  }

  tagInput.value = '';
  v$.value.$reset();
};

const handleRemove = tag => {
  emit('remove-tag', tag);
};
</script>
<style lang="scss" scoped>
.tag-input {
  flex: 0 0 auto;
  max-width: 250px;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: var(--search-bg);
  font-size: 16px;
  outline: none;
}

.tag-input:focus {
  border: 1px solid #ccc;
}

.tag-input::placeholder {
  font-family: inherit;
  color: var(--search-text-color);
  font-size: 0.875rem;
}
</style>
